import { FC } from "react";
import PassengerListingItem from "../common/types/passenger-listing-item";
import usePassengerListingUserPermissions from "../common/user-permissions/use-passenger-listing-user-permissions";
import PassengerListingTableColumn from "../common/types/passenger-listing-table-column";
import passengerListingTableHelper from "./passenger-listing-table.helper";
import userTranslationsHelper from "../../../../../languages/user-translations.helper";
import PassengerListingTableRow from "../common/types/passenger-listing-table-row";
import UserStatus from "../../../../../common/types/user-status";
import TableLinkButtonComponent from "../../../../../common/components/table/button/link/table-link-button.component";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import userRoutesHelper from "../../../common/routes/user-routes.helper";
import TableDeleteButtonComponent from "../../../../../common/components/table/button/delete/table-delete-button.component";
import TableComponent from "../../../../../common/components/table/table.component";
import UserDeleteUserData from "../../../common/delete/common/types/user-delete-user-data";

type PassengerListingTableProps = {
  isError: boolean;
  isLoading: boolean;
  listingItems: PassengerListingItem[];
  onSelectUserToDelete: (userData: UserDeleteUserData) => void;
};

const PassengerListingTableComponent: FC<PassengerListingTableProps> = (
  props
) => {
  const userPermissions = usePassengerListingUserPermissions();

  const columns: PassengerListingTableColumn[] =
    passengerListingTableHelper.getColumns();
  const translations = userTranslationsHelper.getPassengerListingTranslations();

  const createTableRow = (
    listingItem: PassengerListingItem
  ): PassengerListingTableRow => {
    const isEditButtonVisible =
      userPermissions.hasAccessToEditUser &&
      listingItem.status !== UserStatus.DELETED;
    const isDeleteButtonVisible =
      userPermissions.hasAccessToDeleteUser &&
      listingItem.status !== UserStatus.DELETED;
    const activityStatus = passengerListingTableHelper.getStatusText(
      listingItem.status
    );

    return {
      id: listingItem.uuid,
      value: {
        dispatches: (
          <div>
            <div>
              {listingItem.dispatches.map((dispatch, index) => (
                <div
                  key={index}
                  title={`${dispatch.name} (${dispatch.cargoCompany})`}
                >
                  {dispatch.name} ({dispatch.cargoCompany})
                </div>
              ))}
            </div>
          </div>
        ),
        firstName: (
          <div title={listingItem.firstName}>{listingItem.firstName}</div>
        ),
        lastName: (
          <div title={listingItem.lastName}>{listingItem.lastName}</div>
        ),
        username: (
          <div title={listingItem.username}>{listingItem.username}</div>
        ),
        email: <div title={listingItem.email}>{listingItem.email}</div>,
        phoneNumbers: (
          <div>
            <div>
              {listingItem.phoneNumbers.map((phoneNumber, index) => (
                <div key={index} title={phoneNumber}>
                  {phoneNumber}
                </div>
              ))}
            </div>
          </div>
        ),
        addresses: (
          <div>
            <div>
              {listingItem.addresses.map((address, index) => (
                <div key={index} title={address}>
                  {address}
                </div>
              ))}
            </div>
          </div>
        ),
        status: <div title={activityStatus}>{activityStatus}</div>,
        actions: (
          <div className="d-flex">
            {isEditButtonVisible && (
              <TableLinkButtonComponent
                icon={faEdit}
                to={userRoutesHelper.getPassengerEditRoute({
                  passengerUuid: listingItem.uuid,
                })}
                title={translations.table.passengerEditButtonTitle}
                idForTesting={`passenger-listing-table-item-${listingItem.uuid}-edit-button`}
              />
            )}
            {isDeleteButtonVisible && (
              <TableDeleteButtonComponent
                onClick={() =>
                  props.onSelectUserToDelete({
                    uuid: listingItem.uuid,
                    firstName: listingItem.firstName,
                    lastName: listingItem.lastName,
                  })
                }
                title={translations.table.passengerDeleteButtonTitle}
                idForTesting={`passenger-listing-table-item-${listingItem.uuid}-delete-button`}
              />
            )}
          </div>
        ),
      },
    };
  };

  const rows: PassengerListingTableRow[] = props.listingItems.map(
    (listingItem) => createTableRow(listingItem)
  );

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isError={props.isError}
      isLoading={props.isLoading}
    />
  );
};

export default PassengerListingTableComponent;
